import { MIN_PAIRED_BAY_LENGTH, } from "@tedivo/tedivo-bay-grid-pure";
import { getSlotCapabilitiesLegendOptions, } from "@tedivo/tedivo-bay-grid-core";
import { recallBayEditTools, rememberBayEditTools } from "./bayEditToolsMemory";
import EditToolsEnum from "../../../types/EditToolsEnum";
import { createMultiEditButton } from "@tedivo/tedivo-form";
import { getTranslation } from "../../../../../../app/i18/i18tn";
import globalStore from "../../../../../../app/stores/globalStore";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
/**
 * Generates the menu options for the Bay-Edit tools
 * @returns HTMLNode
 */
export class BayEditTopTools extends EventTarget {
    /** Whether the Bay being edited may hold 40'+ definitions. See `can40sBeDefinedInBay` */
    get sizes40Allowance() {
        return this.internalSizes40Allowance;
    }
    set sizes40Allowance(v) {
        this.internalSizes40Allowance = v;
        this.applyChosen();
    }
    get mirrorSlotsWhenDefining() {
        var _a;
        return ((_a = this.nodes.chkMirrorSlotsWhenDefining) === null || _a === void 0 ? void 0 : _a.checked) || false;
    }
    get emphasizedValue() {
        var _a;
        return ((_a = this.nodes.chkEmphasizePairedSlots) === null || _a === void 0 ? void 0 : _a.checked) || false;
    }
    get emphasizeCheckboxDisabled() {
        var _a;
        return ((_a = this.nodes.chkEmphasizePairedSlots) === null || _a === void 0 ? void 0 : _a.disabled) || false;
    }
    set emphasizeCheckboxDisabled(v) {
        if (this.nodes.chkEmphasizePairedSlots) {
            this.nodes.chkEmphasizePairedSlots.disabled = v;
        }
    }
    get mainNode() {
        return this.nodes.mainNode;
    }
    buildHtml() {
        const mainNode = document.createElement("div");
        mainNode.setAttribute("data-set-id", Math.round(Math.random() * 10000).toString());
        const toolsDiv = document.createElement("div");
        toolsDiv.className = "edit-tools";
        // Main buttons
        const label = document.createElement("label");
        label.innerHTML = getTranslation("view:edit.tools.clickAction");
        // flex-holder
        const flexHolderTop = document.createElement("div");
        flexHolderTop.className = "flex-holder-top";
        // Add the buttons to main
        const buttonGroup = document.createElement("sl-radio-group");
        buttonGroup.addEventListener("click", this.onClickMenu, false);
        // Emphasize paired slots
        const chkEmphasizePairedSlots = document.createElement("sl-switch");
        chkEmphasizePairedSlots.innerHTML = getTranslation("view:edit.helpers.highlightPairedSlots");
        chkEmphasizePairedSlots.addEventListener("sl-change", (ev) => {
            const isChecked = ev.target.checked;
            this.dispatchEvent(new CustomEvent("emphasizePairedSlotsChanged", { detail: { isChecked } }));
        }, false);
        // Mirror slots definition
        const chkMirrorSlotsWhenDefining = document.createElement("sl-switch");
        chkMirrorSlotsWhenDefining.innerHTML = getTranslation("view:edit.helpers.mirrorSlotsDefinition");
        chkMirrorSlotsWhenDefining.checked = true;
        chkMirrorSlotsWhenDefining.addEventListener("sl-change", (ev) => {
            const isChecked = ev.target.checked;
            this.dispatchEvent(new CustomEvent("mirrorSlotsDefinitionChanged", { detail: { isChecked } }));
        }, false);
        // Helpers
        const helpersHolder = document.createElement("div");
        helpersHolder.className = "helpers-holder";
        const helpersMainButton = createMultiEditButton({
            id: `helpersCGs`,
            title: getTranslation("view:edit.cgHelpers.title"),
            size: "small",
            variant: "primary",
            showTitleAsTooltip: false,
            iconName: "command",
            options: [
                {
                    value: "copyFromBay",
                    name: getTranslation("view:edit.helpers.copyFromBay.title"),
                    action: () => {
                        this.dispatchEvent(new CustomEvent("copyFromBay"));
                    },
                },
                {
                    value: "copyToBays",
                    name: getTranslation("view:edit.helpers.copyToBays.title"),
                    action: () => {
                        this.dispatchEvent(new CustomEvent("copyToBays"));
                    },
                },
                {
                    value: "remapTiers",
                    name: getTranslation("view:edit.helpers.remapTiers.title"),
                    action: () => {
                        this.dispatchEvent(new CustomEvent("remapTiers"));
                    },
                },
                {
                    value: "move40Definitions",
                    name: getTranslation("view:edit.helpers.move40Definitions.title"),
                    action: () => {
                        this.dispatchEvent(new CustomEvent("move40Definitions"));
                    },
                },
            ],
        });
        helpersHolder.appendChild(helpersMainButton);
        // Options for each button
        const toolsOptionsDiv = document.createElement("div");
        toolsOptionsDiv.className = "tools-options";
        flexHolderTop.appendChild(buttonGroup);
        flexHolderTop.appendChild(helpersHolder);
        flexHolderTop.appendChild(chkMirrorSlotsWhenDefining);
        flexHolderTop.appendChild(chkEmphasizePairedSlots);
        return {
            mainNode,
            toolsDiv,
            label,
            flexHolderTop,
            buttonGroup,
            chkEmphasizePairedSlots,
            chkMirrorSlotsWhenDefining,
            helpersHolder,
            toolsOptionsDiv,
        };
    }
    /**
     * Rebuilds the tools for the Bay being opened
     * @param vesselKey Identifies the vessel, so the user's picks carry over to its
     * other Bays. Without it, every Bay starts from the defaults
     */
    updateToolsOptions(containersLengths, featuresAllowed, sizes40Allowance = { allowed: true }, vesselKey) {
        var _a;
        const buttonGroup = this.nodes.buttonGroup;
        const toolsOptionsDiv = this.nodes.toolsOptionsDiv;
        this.containersLengths = containersLengths;
        this.featuresAllowed = featuresAllowed;
        this.internalSizes40Allowance = sizes40Allowance;
        this.vesselKey = vesselKey;
        const recalled = vesselKey === undefined ? undefined : recallBayEditTools(vesselKey);
        removeChildren(buttonGroup);
        removeChildren(toolsOptionsDiv);
        this.menuOptions = getMenuOptions(this.containersLengths || [], this.featuresAllowed, globalStore.resolvedColorMode === "dark");
        // "Special" comes and goes with `featuresAllowed`, so the remembered tool may be gone
        const activeTool = recalled !== undefined && this.menuOptions.some((o) => o.id === recalled.activeTool)
            ? recalled.activeTool
            : (_a = this.menuOptions[0]) === null || _a === void 0 ? void 0 : _a.id;
        this.state.activeTool = activeTool;
        buttonGroup.value = String(activeTool);
        this.menuOptions.forEach((option) => {
            var _a, _b;
            const button = document.createElement("sl-radio-button");
            button.value = String(option.id);
            button.pill = true;
            button.innerHTML = getTranslation(option.label);
            buttonGroup.appendChild(button);
            this.chosen[option.id] = [...((_b = (_a = recalled === null || recalled === void 0 ? void 0 : recalled.tools[option.id]) !== null && _a !== void 0 ? _a : option.initial) !== null && _b !== void 0 ? _b : [])];
            this.state.tools[option.id] = [];
            const d = document.createElement("div");
            d.className = "tools-options-inst";
            d.dataset.value = String(option.id);
            toolsOptionsDiv.appendChild(d);
            d.style.display = option.id === activeTool ? "flex" : "none";
            // Disabled options swallow their own "sl-change", so the explanation has to
            // be hooked on the way down
            d.addEventListener("click", this.onClickBlockedSize, true);
            // Add the toolsOptions to each option
            if (option.optionsControl) {
                const parent = option.optionsControl === "sl-radio"
                    ? document.createElement("sl-radio-group")
                    : d;
                if (option.getOptions) {
                    const toolsOptions = option.getOptions();
                    toolsOptions.forEach((v) => {
                        this.createSubOption(option, v, parent);
                    });
                }
                if (option.optionsControl === "sl-radio") {
                    d.appendChild(parent);
                    // sl-radio-group uses "value" (v2.0.82), not individual sl-radios to
                    // assign value. It is set in `applyChosen`. Same version needs listener
                    parent.addEventListener("sl-change", () => {
                        // A fallback picked by `applyChosen` is not the user's choice
                        if (this.isApplyingChosen)
                            return;
                        const state = this.state;
                        const menuId = option.id;
                        const value = parent
                            .value;
                        this.chosen[menuId] = [value];
                        this.remember();
                        if (state.tools[menuId][0] === value)
                            return;
                        state.tools[option.id] = [value];
                        this.dispatchEvent(new CustomEvent("optionChanged", {
                            detail: { menuId, optionId: value, state },
                        }));
                    }, false);
                }
            }
        });
        this.applyChosen();
    }
    constructor() {
        super();
        this.state = {
            activeTool: EditToolsEnum.ADD_MANY,
            tools: {
                [EditToolsEnum.ADD_MANY]: [],
                [EditToolsEnum.ADD_REMOVE_SINGLE]: [],
                [EditToolsEnum.CLEAR]: [],
                [EditToolsEnum.ADD_REMOVE_RESTRICTED]: [],
                [EditToolsEnum.ADD_REMOVE_SPECIAL]: [],
            },
        };
        this.containersLengths = [];
        this.featuresAllowed = undefined;
        this.internalSizes40Allowance = { allowed: true };
        this.menuOptions = [];
        this.vesselKey = undefined;
        /** What the user picked. `state.tools` is this minus what the Bay blocks */
        this.chosen = {
            [EditToolsEnum.ADD_MANY]: [],
            [EditToolsEnum.ADD_REMOVE_SINGLE]: [],
            [EditToolsEnum.CLEAR]: [],
            [EditToolsEnum.ADD_REMOVE_RESTRICTED]: [],
            [EditToolsEnum.ADD_REMOVE_SPECIAL]: [],
        };
        this.isApplyingChosen = false;
        this.onClickMenu = (ev) => {
            const target = ev.target;
            const value = target.value;
            if (!value)
                return;
            this.state.activeTool = Number(value);
            this.remember();
            // hack
            document
                .querySelectorAll(".tools-options-inst")
                .forEach((d) => {
                d.style.display = d.dataset.value === value ? "flex" : "none";
            });
        };
        this.onClickOption = (ev, menuId, optionId, optionsControl) => {
            const state = this.state;
            const chosen = this.chosen[menuId];
            this.chosen[menuId] =
                optionsControl !== "sl-checkbox"
                    ? [optionId]
                    : chosen.indexOf(optionId) >= 0
                        ? chosen.filter((v) => v !== optionId)
                        : [...chosen, optionId];
            this.remember();
            if (optionsControl === "sl-checkbox") {
                if (state.tools[menuId].indexOf(optionId) >= 0) {
                    state.tools[menuId] = state.tools[menuId].filter((v) => v !== optionId);
                }
                else {
                    state.tools[menuId].push(optionId);
                }
            }
            else {
                if (state.tools[menuId][0] === optionId)
                    return;
                state.tools[menuId] = [optionId];
            }
            this.dispatchEvent(new CustomEvent("optionChanged", {
                detail: { menuId, optionId, state },
            }));
        };
        this.onClickBlockedSize = (ev) => {
            if (this.internalSizes40Allowance.allowed)
                return;
            const hitBlocked = ev
                .composedPath()
                .some((node) => node instanceof HTMLElement &&
                node.dataset.size40 === "1" &&
                node.disabled);
            if (!hitBlocked)
                return;
            ev.preventDefault();
            ev.stopPropagation();
            this.dispatchEvent(new CustomEvent("blocked40sClicked"));
        };
        this.nodes = this.buildHtml();
        const { mainNode, toolsDiv, label, flexHolderTop, chkEmphasizePairedSlots, toolsOptionsDiv, } = this.nodes;
        toolsDiv.appendChild(label);
        toolsDiv.appendChild(flexHolderTop);
        toolsDiv.appendChild(toolsOptionsDiv);
        // Start with emphasized slots of pairedBay
        chkEmphasizePairedSlots.checked = true;
        chkEmphasizePairedSlots.dispatchEvent(new Event("sl-change"));
        mainNode.appendChild(toolsDiv);
    }
    createSubOption(option, v, parent) {
        if (option === undefined || option.optionsControl === undefined)
            return;
        const optionsControl = option.optionsControl;
        const ctrl = document.createElement(optionsControl);
        ctrl.appendChild(createSubLabel(`${getTranslation(v.labelKey)} ${v.labelKey.indexOf("misc") > 0
            ? `- ${v.labelKey.split(".").pop()}`
            : ""}`, v.bgColor));
        ctrl.value = v.id;
        ctrl.dataset.menuId = String(option.id);
        // Disabled, checked and the state are all worked out in `applyChosen`
        if (optionNeedsPairedBay(v.id))
            ctrl.dataset.size40 = "1";
        if (optionsControl === "sl-checkbox")
            ctrl.addEventListener("sl-change", (ev) => this.onClickOption(ev, option.id, v.id, optionsControl), false);
        parent.appendChild(ctrl);
    }
    /**
     * Puts the user's picks on screen, minus what the Bay blocks, and derives the
     * effective `state.tools` from them. A blocked option must stay out of the
     * state, or `modifyCellsWithActions` would apply it. The picks themselves are
     * left alone, so a blocked 40' comes back in the next Bay that allows it
     */
    applyChosen() {
        const allowed = this.internalSizes40Allowance.allowed;
        const state = this.state;
        this.isApplyingChosen = true;
        this.menuOptions.forEach((option) => {
            var _a, _b, _c;
            const optionsControl = option.optionsControl;
            if (optionsControl === undefined)
                return;
            const holder = this.nodes.toolsOptionsDiv.querySelector(`.tools-options-inst[data-value="${option.id}"]`);
            if (!holder)
                return;
            const ctrls = Array.from(holder.querySelectorAll(optionsControl));
            ctrls.forEach((ctrl) => {
                ctrl.disabled = ctrl.dataset.size40 === "1" && !allowed;
            });
            const chosen = this.chosen[option.id];
            if (optionsControl === "sl-checkbox") {
                ctrls.forEach((ctrl) => {
                    ctrl.checked =
                        !ctrl.disabled && chosen.indexOf(ctrl.value) >= 0;
                });
                state.tools[option.id] = ctrls
                    .filter((ctrl) => ctrl.checked)
                    .map((ctrl) => ctrl.value);
                return;
            }
            // Radios: the pick, else the default, else whatever is left enabled
            const isEnabled = (v) => v !== undefined && ctrls.some((ctrl) => ctrl.value === v && !ctrl.disabled);
            const value = ((_b = [chosen[0], (_a = option.initial) === null || _a === void 0 ? void 0 : _a[0]].find(isEnabled)) !== null && _b !== void 0 ? _b : (_c = ctrls.find((ctrl) => !ctrl.disabled)) === null || _c === void 0 ? void 0 : _c.value);
            const group = holder.querySelector("sl-radio-group");
            if (group && value !== undefined)
                group.value = value;
            state.tools[option.id] = value === undefined ? [] : [value];
        });
        this.isApplyingChosen = false;
    }
    remember() {
        if (this.vesselKey === undefined)
            return;
        rememberBayEditTools(this.vesselKey, this.state.activeTool, this.chosen);
    }
}
const SIZE_OPTION_RE = /^ADD_SIZE_(\d+)(_CONEREQ)?$/;
/** Sizes of 40' and over span two bays, so they need a paired one */
function optionNeedsPairedBay(id) {
    const match = SIZE_OPTION_RE.exec(id);
    return match !== null && Number(match[1]) >= MIN_PAIRED_BAY_LENGTH;
}
function createSubLabel(label, bgColor) {
    const sp = document.createElement("span");
    const em = document.createElement("em");
    sp.innerHTML = label;
    sp.style.position = "relative";
    em.style.backgroundColor = bgColor;
    em.style.display = "inline-block";
    em.style.position = "absolute";
    em.style.width = "100%";
    em.style.height = "2px";
    em.style.bottom = "-1px";
    em.style.left = "0";
    em.style.borderRadius = "2px";
    sp.appendChild(em);
    sp.style.pointerEvents = "none";
    return sp;
}
function getMenuOptions(availableLengths, featuresAllowed = {}, isDarkMode = false) {
    const options = [
        {
            id: EditToolsEnum.ADD_MANY,
            label: "view:edit.tools.addMany",
            optionsControl: "sl-checkbox",
            initial: ["ADD_SIZE_20", "ADD_SIZE_40"],
            getOptions: () => getSlotCapabilitiesLegendOptions(availableLengths, ["sizes", "misc"], featuresAllowed, isDarkMode),
        },
        {
            id: EditToolsEnum.ADD_REMOVE_SINGLE,
            label: "view:edit.tools.addRemoveSingle",
            optionsControl: "sl-radio",
            initial: ["ADD_SIZE_20"],
            getOptions: () => getSlotCapabilitiesLegendOptions(availableLengths, ["sizes", "misc"], featuresAllowed, isDarkMode),
        },
        featuresAllowed.slotConeRequired
            ? {
                id: EditToolsEnum.ADD_REMOVE_SPECIAL,
                label: "view:edit.tools.special",
                optionsControl: "sl-radio",
                initial: ["ADD_SIZE_20_CONEREQ"],
                getOptions: () => getSlotCapabilitiesLegendOptions(availableLengths, ["sizesWithCones"], featuresAllowed, isDarkMode),
            }
            : undefined,
        {
            id: EditToolsEnum.ADD_REMOVE_RESTRICTED,
            label: "view:edit.tools.restricted",
        },
        {
            id: EditToolsEnum.CLEAR,
            label: "view:edit.tools.clear",
        },
    ].filter(Boolean);
    return options;
}
